/**
 * The household's categories, from the Categories tab of the Sheet.
 *
 * Each row has a stable `category_id` (what Transactions, Corrections and Rules
 * store), a `category` name, an optional `subcategory`, and a `type` that says
 * how money in it is counted. Renaming a row, or moving it under a parent by
 * filling in category and subcategory, keeps every transaction filed under its
 * id. A row typed into the Sheet without an id gets one from its names.
 *
 * Charts group by `category` (the parent); the drawer splits a parent into its
 * subcategories. The tab starts with the built-in categories Claude has always
 * used; an empty tab reads as those.
 */
import { CATEGORIES } from './extraction'
import type { Database, Row } from './google/sheets'

export type CategoryType = 'spending' | 'income' | 'transfer' | 'investing'
export const CATEGORY_TYPES: CategoryType[] = ['spending', 'income', 'transfer', 'investing']

export interface Category {
  id: string
  category: string
  subcategory: string
  type: CategoryType
}

/** A category the person is adding; it gets an id when saved. */
export type NewCategory = Omit<Category, 'id'>

const NAMES: Record<string, string> = { gifts_donations: 'Gifts & donations' }
const builtInName = (id: string) => NAMES[id] ?? id.replace(/[_-]+/g, ' ').trim().replace(/^./, (c) => c.toUpperCase())
const BUILT_IN_TYPE: Record<string, CategoryType> = { income: 'income', transfer: 'transfer', investments: 'investing' }

export const BUILT_IN: Category[] = CATEGORIES.map((id) => ({ id, category: builtInName(id), subcategory: '', type: BUILT_IN_TYPE[id] ?? 'spending' }))

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v).trim())
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
const idFor = (category: string, subcategory: string) => slug(subcategory ? `${category} ${subcategory}` : category)

/** A type cell read loosely ("Transfers", "Internal transfer"); undefined when blank or unrecognised. */
function typeFrom(value: unknown): CategoryType | undefined {
  const t = str(value).toLowerCase()
  if (t.includes('transfer')) return 'transfer'
  if (t.includes('invest')) return 'investing'
  if (t.includes('income')) return 'income'
  if (t.includes('spend') || t.includes('expense')) return 'spending'
  return undefined
}

const builtInType = (id: string): CategoryType => BUILT_IN.find((c) => c.id === id)?.type ?? 'spending'

export function toRow(c: Category): Row {
  return { category_id: c.id, category: c.category, subcategory: c.subcategory, type: c.type }
}

export const seedRows = (): Row[] => BUILT_IN.map(toRow)

export interface Catalog {
  /** The tab's rows in order, or the built-ins when it is empty. */
  list: Category[]
  /** "Cars / Fuel", or "Dining" for a row with no subcategory. */
  label(id: string): string
  /** The parent the charts group by: "Cars". */
  group(id: string): string
  /** Within its group: "Fuel", or the group's name for the parent's own row. */
  sub(id: string): string
  type(id: string): CategoryType
  /** The id of an existing category with these names, ignoring case. */
  find(category: string, subcategory: string): string | undefined
}

export function catalog(rows: Row[] = []): Catalog {
  const list: Category[] = []
  const seen = new Set<string>()
  // One spelling per parent, the first one met, so "cars" and "Cars" are one group.
  const spelling = new Map<string, string>()
  for (const r of rows) {
    const name = str(r.category)
    const sub = str(r.subcategory)
    const id = str(r.category_id) || (name ? idFor(name, sub) : '')
    if (!id || seen.has(id)) continue
    seen.add(id)
    const category = name || builtInName(id)
    const key = category.toLowerCase()
    if (!spelling.has(key)) spelling.set(key, category)
    // A built-in whose type cell is blank or unreadable keeps its own type, so a
    // retyped transfer row never turns card payments into spending.
    list.push({ id, category: spelling.get(key)!, subcategory: sub, type: typeFrom(r.type) ?? builtInType(id) })
  }
  if (!list.length) list.push(...BUILT_IN)
  const byId = new Map(list.map((c) => [c.id, c]))
  // A transaction filed under an id the tab no longer has still reads sensibly.
  const of = (id: string): Category => byId.get(id) ?? BUILT_IN.find((c) => c.id === id) ?? { id, category: builtInName(id || 'other'), subcategory: '', type: 'spending' }
  return {
    list,
    label: (id) => {
      const c = of(id)
      return c.subcategory ? `${c.category} / ${c.subcategory}` : c.category
    },
    group: (id) => of(id).category,
    sub: (id) => of(id).subcategory || of(id).category,
    type: (id) => of(id).type,
    find: (category, subcategory) =>
      list.find((c) => c.category.toLowerCase() === category.trim().toLowerCase() && c.subcategory.toLowerCase() === subcategory.trim().toLowerCase())?.id,
  }
}

const byName = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true }).compare
// Every picker in the drawer (up to 400 rows) asks for this on each render.
const groupedCache = new WeakMap<Catalog, { group: string; items: Category[] }[]>()

/**
 * The groups alphabetically, each with its categories: the parent's own row
 * first, then its subcategories alphabetically. What a picker lists, whatever
 * order the tab is in.
 */
export function grouped(cats: Catalog): { group: string; items: Category[] }[] {
  const hit = groupedCache.get(cats)
  if (hit) return hit
  const out = new Map<string, Category[]>()
  for (const c of cats.list) out.set(c.category, [...(out.get(c.category) ?? []), c])
  const result = [...out]
    .sort(([a], [b]) => byName(a, b))
    .map(([group, items]) => ({ group, items: [...items].sort((a, b) => (!a.subcategory ? -1 : !b.subcategory ? 1 : byName(a.subcategory, b.subcategory))) }))
  groupedCache.set(cats, result)
  return result
}

/** The ids Claude may file a transaction under: the tab's, plus the two its instructions name. */
export function extractionIds(cats: Catalog): [string, ...string[]] {
  return [...new Set(['transfer', 'other', ...cats.list.map((c) => c.id)])] as [string, ...string[]]
}

/** One line per category for Claude: `fuel = Cars / Fuel (spending)`. */
export function describeForClaude(cats: Catalog): string {
  return cats.list.map((c) => `${c.id} = ${cats.label(c.id)}${c.type === 'spending' ? '' : ` (${c.type})`}`).join('; ')
}

/**
 * Adds a category to the tab and returns its id; one that already exists, by
 * name, is reused. An empty tab gets the built-ins first, so adding one
 * doesn't hide the rest.
 */
export async function saveCategory(db: Database, input: NewCategory): Promise<string> {
  const category = input.category.trim()
  const subcategory = input.subcategory.trim()
  if (!category) throw new Error('A category needs a name')
  const rows = (await db.read('Categories')).rows
  const cats = catalog(rows)
  const existing = cats.find(category, subcategory)
  if (existing) return existing
  // Built-in ids stay reserved even when deleted from the tab: old rows, Claude's
  // instructions and the ledger still give them their built-in meaning.
  const taken = new Set([...cats.list.map((c) => c.id), ...BUILT_IN.map((c) => c.id)])
  const base = idFor(category, subcategory) || 'category'
  let id = base
  for (let n = 2; taken.has(id); n++) id = `${base}-${n}`
  // Spelled as the existing parent is, so it joins that group.
  const parent = cats.list.find((c) => c.category.toLowerCase() === category.toLowerCase())?.category ?? category
  const added = toRow({ id, category: parent, subcategory, type: input.type })
  await db.append('Categories', rows.some((r) => str(r.category_id) || str(r.category)) ? [added] : [...seedRows(), added])
  return id
}
